<script setup lang="ts">
// Navigation items
import type { InnerNavItem } from '@/types/all'
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const props = defineProps<{
  innerNavItems: InnerNavItem[]
  activeInnerTab: number
}>()

const router = useRouter()
const route = useRoute()
const getCurrentRouteIndex = computed(() => {
  // For the mobile dropdown, find the current route index
  if (route.name === 'dashboard-help') {
    return props.innerNavItems.length
  }

  const index = props.innerNavItems.findIndex((item) => item.route === route.name)
  return index >= 0 ? index : 0
})

const handleMobileNavigation = (event: Event) => {
  const selectedIndex = parseInt((event.target as HTMLSelectElement).value)
  if (selectedIndex === props.innerNavItems.length) {
    router.push({ name: 'dashboard-help' })
  } else {
    router.push({ name: props.innerNavItems[selectedIndex].route })
  }
}
</script>

<template>
  <div id="dashboardInnerNav" class="bg-white shadow rounded-lg mb-6 overflow-hidden">
    <!-- Desktop Navigation -->
    <div class="hidden sm:block">
      <nav class="flex">
        <div class="flex">
          <div v-for="(item, index) in innerNavItems" :key="index" class="relative">
            <router-link
              :to="{ name: item.route }"
              class="px-6 py-4 inline-block text-sm font-medium transition-colors duration-200"
              :class="[
                route.name === item.route
                  ? 'text-blue-600'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50',
              ]"
            >
              {{ item.label }}
            </router-link>
            <!-- Active Indicator -->
            <div
              v-if="route.name === item.route"
              class="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"
            ></div>
          </div>
        </div>
        <div class="ml-auto">
          <router-link
            :to="{ name: 'dashboard-help' }"
            class="px-6 py-4 inline-flex items-center text-sm font-medium transition-colors duration-200 rounded-md"
            :class="[
              route.name === 'dashboard-help'
                ? 'text-blue-600 bg-blue-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50',
            ]"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 mr-2"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
            Help
          </router-link>
        </div>
      </nav>
    </div>
    <!-- Mobile Navigation (Dropdown) -->
    <div class="block sm:hidden relative">
      <select
        @change="handleMobileNavigation"
        class="w-full pl-4 pr-10 py-3 border-0 focus:ring-0 focus:outline-none text-base"
        :value="getCurrentRouteIndex"
      >
        <option v-for="(item, index) in innerNavItems" :key="index" :value="index">
          {{ item.label }}
        </option>
        <option :value="innerNavItems.length">Help</option>
      </select>
      <div class="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
        <svg class="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>
    </div>
  </div>
</template>
